import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRootStore } from "../state/RootStore";
import {
  APP_TO_BUILD,
  NUM_SECTIONS,
  PRACTICE_TYPE_DATA,
  PracticeTestType,
  SECTION_ID_TO_PRACTICE_TEST_TYPE,
  TEST_TYPE_TO_RESULT_TITLE,
} from "../../appsConfig";
import {
  getLocalizedPracticeTypeResultTitle,
  localizePracticeTypeData,
} from "@/i18n/practiceTypeCopy";
import { resolveStateAwareNumber } from "@/utils/curriculum";
import { getExamDisplayNames } from "@/utils/examSelection";

const SECTION_COLORS = [
  "#ffcc03",
  "#ff9500",
  "#34c759",
  "#30b0c7",
  "#5856d6",
  "#ff2d55",
  "#c44ed4",
];

interface ExamBreakdownProps {
  forceStateCode?: string;
}

export const ExamBreakdown = ({ forceStateCode }: ExamBreakdownProps) => {
  const store = useRootStore();
  const { t } = useTranslation();
  const stateCode = forceStateCode || store.mainAppStore.selectedStateCode || "";
  const stateInfo = store.questionsStore.stateInfo;

  const topics = useMemo(() => {
    const weights = Array.from({ length: NUM_SECTIONS }, (_, index) => {
      const raw = store.questionsStore.curriculum[String(index + 1)]?.per_of_q;
      return PracticeTestType.byState
        ? resolveStateAwareNumber(raw, stateCode, 0)
        : resolveStateAwareNumber(raw, undefined, 0);
    });
    const total = weights.reduce((sum, value) => sum + value, 0) || 1;

    return weights.flatMap((weight, index) => {
      if (weight <= 0) return [];
      const sectionId = index + 1;
      const testType = SECTION_ID_TO_PRACTICE_TEST_TYPE[sectionId];
      const practiceData = localizePracticeTypeData(
        testType,
        PRACTICE_TYPE_DATA[testType] || {},
      );

      return [{
        color: SECTION_COLORS[index] || SECTION_COLORS[0],
        description: practiceData?.breakdownDescription || practiceData?.description,
        percentage: Math.round((weight / total) * 100),
        title: getLocalizedPracticeTypeResultTitle(
          testType,
          TEST_TYPE_TO_RESULT_TITLE[testType],
        ),
      }];
    });
  }, [stateCode, store.questionsStore.curriculum]);

  const rawExamName = stateInfo?.stateExamNames?.[stateCode] || "";
  const rawStateName = stateInfo?.stateNames?.[stateCode] || "";
  const displayNames = APP_TO_BUILD === "electrical_us"
    ? getExamDisplayNames(rawExamName, rawStateName)
    : null;
  const examPracticeData = PRACTICE_TYPE_DATA[PracticeTestType.examType] || {};
  const fallbackExamName = getLocalizedPracticeTypeResultTitle(
    PracticeTestType.examType,
    examPracticeData.resultsTitle || examPracticeData.title || t("examBreakdown.title"),
  );
  const selectedExamName = displayNames
    ? `${displayNames.stateName} ${displayNames.examLabel}`.trim()
    : rawExamName;
  const examName = selectedExamName || fallbackExamName;
  const source = displayNames?.stateName || rawStateName;
  const questionCount = store.questionsStore.getNumExamTypeQuestions(stateCode);
  const passingScore = Math.round(
    store.questionsStore.getPassingScore(stateCode) * 100,
  );
  const allottedTime = store.questionsStore.getTotalExamTimeMinutes(stateCode);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold leading-[26px] text-[#0a0c11]">
          {examName}
        </h2>
        {source && (
          <p className="mt-1 text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
            <span className="font-semibold">{t("examBreakdown.source_label")}:</span>{" "}
            {source}
          </p>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          [questionCount, t("examBreakdown.questions_label")],
          [`${passingScore}%`, t("examBreakdown.passing_score_label")],
          [`${allottedTime}m`, t("examBreakdown.allotted_time_label")],
        ].map(([value, label]) => (
          <div
            className="rounded-2xl border-[1.5px] border-[#ebecf0] bg-[#f4f4f6] px-6 py-4 text-center"
            key={String(label)}
          >
            <dd className="text-lg font-semibold leading-6 text-[#0a0c11]">{value}</dd>
            <dt className="mt-1 text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{label}</dt>
          </div>
        ))}
      </dl>

      <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
        {topics.map((topic) => (
          <article className="flex min-h-[132px] flex-col rounded-2xl border border-[#dddfe4] p-4" key={topic.title}>
            <div className="flex items-start justify-between gap-3 text-[#0a0c11]">
              <h3 className="text-sm font-bold leading-[21px] tracking-[0.07px]">{topic.title}</h3>
              <span className="shrink-0 text-base font-semibold leading-6">{topic.percentage}%</span>
            </div>
            <p className="mt-1 flex-1 text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
              {topic.description}
            </p>
            <div className="mt-3 h-[10px] overflow-hidden rounded-full bg-[#efefef]">
              <div
                className="h-full rounded-full"
                style={{ backgroundColor: topic.color, width: `${topic.percentage}%` }}
              />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ExamBreakdown;
